import { useState } from 'react'
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Globe, Zap, Sparkles, Star, ChevronLeft, ChevronRight } from 'lucide-react'
import type { DashboardReview, Platform, ReviewStatus } from '../../types/dashboard'
import { deriveWorkflowTag, isQualityOnlyEscalation, isUrgentEscalation } from '../../types/dashboard'

interface ReviewTableProps {
  reviews: DashboardReview[]
  isLoading: boolean
  isFetchingLive?: boolean
  lastSyncedAt: Date | null
  availablePlatforms: Platform[]
  selectedPlatform: Platform
  onPlatformChange: (p: Platform) => void
  onRowClick: (id: string) => void
  onFetchLive?: () => void
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  onClearSelection?: () => void
  availableRatings?: number[]
  selectedRating?: number | 'all'
  onRatingChange?: (rating: number | 'all') => void
  onBulkGenerate?: () => void
  isBulkGenerating?: boolean
}

type SortField = 'date' | 'rating'
type SortDir = 'asc' | 'desc'

const PAGE_SIZE = 10

const GRID_COLUMNS_WITH_SELECT = '24px 2fr 80px 100px 90px 130px 80px 24px'
const GRID_COLUMNS_NO_SELECT = '2fr 80px 100px 90px 130px 80px 24px'

const PLATFORM_LABELS: Record<string, string> = {
  google: 'Google Reviews',
  judgeme: 'Judge.me',
}

const STATUS_LABELS: Record<ReviewStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  pending_approval: 'Pending approval',
  responded: 'Posted',
  escalated: 'Escalated',
  failed: 'Failed',
  skipped: 'Skipped',
}

const STATUS_CLASSES: Record<ReviewStatus, string> = {
  pending: 'bg-white/5 text-rd-muted',
  processing: 'bg-rd-accent2/10 text-rd-accent2',
  pending_approval: 'bg-warning-500/10 text-warning-500',
  responded: 'bg-rd-accent/10 text-rd-accent',
  escalated: 'bg-danger-500/10 text-danger-500',
  failed: 'bg-danger-500/10 text-danger-500',
  skipped: 'bg-white/5 text-rd-dim',
}

const SENTIMENT_CLASSES: Record<string, string> = {
  positive: 'bg-rd-accent/10 text-rd-accent',
  neutral: 'bg-warning-500/10 text-warning-500',
  negative: 'bg-danger-500/10 text-danger-500',
}

const ROW_BORDER: Record<ReviewStatus, string> = {
  pending: 'border-l-transparent',
  processing: 'border-l-rd-accent2/50',
  pending_approval: 'border-l-warning-500/60',
  responded: 'border-l-rd-accent/60',
  escalated: 'border-l-danger-500',
  failed: 'border-l-danger-500/60',
  skipped: 'border-l-transparent',
}

// A quality-only escalation (e.g. a 5-star review whose auto-generated reply
// came out too short) isn't a bad review — it shouldn't get the same red
// "something's wrong" treatment as a genuine negative-review escalation.
const QUALITY_ONLY_LABEL = 'Needs a quick look'
const QUALITY_ONLY_CLASS = 'bg-warning-500/10 text-warning-500'
const QUALITY_ONLY_BORDER = 'border-l-warning-500/60'

function statusLabel(review: DashboardReview): string {
  return isQualityOnlyEscalation(review) ? QUALITY_ONLY_LABEL : STATUS_LABELS[review.status]
}
function statusClass(review: DashboardReview): string {
  return isQualityOnlyEscalation(review) ? QUALITY_ONLY_CLASS : STATUS_CLASSES[review.status]
}
function rowBorderClass(review: DashboardReview): string {
  return isQualityOnlyEscalation(review) ? QUALITY_ONLY_BORDER : ROW_BORDER[review.status]
}

function Stars({ rating }: { rating: number | null }) {
  if (!rating) return <span className="text-rd-dim text-xs">—</span>
  return (
    <span className="flex items-center gap-px text-[11px]">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < rating ? 'text-warning-500' : 'text-rd-dim'}>★</span>
      ))}
    </span>
  )
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${className}`}>
      {children}
    </span>
  )
}

function SortHeader({
  label, active, dir, onClick,
}: { label: string; active: boolean; dir: SortDir; onClick: () => void }) {
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <button
      type="button"
      onClick={onClick}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`flex items-center gap-1 hover:text-rd-muted transition-colors ${active ? 'text-rd-muted' : ''}`}
    >
      {label}
      <Icon className="w-3 h-3" />
    </button>
  )
}

function timeSince(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}

export default function ReviewTable({
  reviews,
  isLoading,
  isFetchingLive = false,
  lastSyncedAt,
  availablePlatforms,
  selectedPlatform,
  onPlatformChange,
  onRowClick,
  onFetchLive,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onClearSelection,
  availableRatings,
  selectedRating = 'all',
  onRatingChange,
  onBulkGenerate,
  isBulkGenerating = false,
}: ReviewTableProps) {
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [page, setPage] = useState(1)

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
    setPage(1)
  }

  const sortGroup = (rows: DashboardReview[]): DashboardReview[] => {
    if (!sortField) return rows
    const factor = sortDir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      if (sortField === 'rating') return ((a.rating ?? 0) - (b.rating ?? 0)) * factor
      const at = a.review_date ? new Date(a.review_date).getTime() : 0
      const bt = b.review_date ? new Date(b.review_date).getTime() : 0
      return (at - bt) * factor
    })
  }

  // Selected rows always stay pinned at the very top — otherwise a multi-page
  // selection is invisible until you page through to find each checked row.
  // Escalated reviews are pinned next, ahead of everything else. Sorting only
  // reorders within each of the three groups.
  const selectedIdSet = selectedIds ?? new Set<string>()
  const selected = sortGroup(reviews.filter((r) => selectedIdSet.has(r.id)))
  const escalated = sortGroup(reviews.filter((r) => isUrgentEscalation(r) && !selectedIdSet.has(r.id)))
  const rest = sortGroup(reviews.filter((r) => !isUrgentEscalation(r) && !selectedIdSet.has(r.id)))
  const sorted = [...selected, ...escalated, ...rest]

  // A review that's already been posted can't be regenerated — exclude it
  // from anything selection-related so "N selected" never silently shrinks.
  const isSelectable = (r: DashboardReview) => r.status !== 'responded'
  const selectable = sorted.filter(isSelectable)

  const selectionEnabled = Boolean(selectedIds && onToggleSelect && onToggleSelectAll)
  const gridColumns = selectionEnabled ? GRID_COLUMNS_WITH_SELECT : GRID_COLUMNS_NO_SELECT
  const selectedCount = selectedIds?.size ?? 0
  const allSelected = selectionEnabled && selectable.length > 0 && selectedCount === selectable.length

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-rd-surface border border-white/[0.08] rounded-xl min-h-0">

      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.07] flex-shrink-0">
        <div>
          <h2 className="text-[15px] font-medium text-rd-text">Reviews</h2>
          <p className="text-[11px] text-rd-dim mt-0.5">
            {lastSyncedAt ? `Last synced ${timeSince(lastSyncedAt)}` : 'Not synced yet'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Platform filter */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-rd-surface2 border border-white/[0.08] rounded-lg text-[12px] text-rd-muted">
            <Globe className="w-3.5 h-3.5 text-rd-accent2" />
            <select
              value={selectedPlatform}
              onChange={(e) => onPlatformChange(e.target.value as Platform)}
              className="bg-transparent border-none outline-none text-rd-text text-[12px] cursor-pointer"
            >
              <option value="all" style={{ background: '#1a1a26' }}>All platforms</option>
              {availablePlatforms.map((p) => (
                <option key={p} value={p} style={{ background: '#1a1a26' }}>
                  {PLATFORM_LABELS[p] ?? p}
                </option>
              ))}
            </select>
          </div>

          {/* Rating filter — narrows the table to a single star rating */}
          {availableRatings && availableRatings.length > 0 && onRatingChange && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-rd-surface2 border border-white/[0.08] rounded-lg text-[12px] text-rd-muted">
              <Star className="w-3.5 h-3.5 text-warning-500" />
              <select
                value={selectedRating}
                onChange={(e) => onRatingChange(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="bg-transparent border-none outline-none text-rd-text text-[12px] cursor-pointer"
              >
                <option value="all" style={{ background: '#1a1a26' }}>All ratings</option>
                {availableRatings.map((r) => (
                  <option key={r} value={r} style={{ background: '#1a1a26' }}>
                    {r}★ only
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Fetch live review button — shown when handler is wired up */}
          {onFetchLive && (
            <button
              type="button"
              onClick={onFetchLive}
              disabled={isFetchingLive}
              title="Import one real review from Google for your business and generate an AI response for it"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium text-rd-accent border border-rd-accent/30 bg-rd-accent/10 hover:bg-rd-accent/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Zap className={`w-3.5 h-3.5 ${isFetchingLive ? 'animate-pulse' : ''}`} />
              {isFetchingLive ? 'Fetching…' : 'Fetch live review'}
            </button>
          )}
        </div>
      </div>

      {/* Escalation banner */}
      {escalated.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-danger-500/5 border-b border-danger-500/20 flex-shrink-0">
          <AlertTriangle className="w-3.5 h-3.5 text-danger-500 flex-shrink-0" />
          <p className="text-[12px] text-danger-500">
            <span className="font-medium">{escalated.length} escalated {escalated.length === 1 ? 'review' : 'reviews'}</span>
            {' '}— pinned to the top and require your attention.
          </p>
        </div>
      )}

      {/* Bulk-action bar — shown once at least one review is selected */}
      {selectionEnabled && selectedCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-rd-accent/5 border-b border-rd-accent/20 flex-shrink-0">
          <p className="text-[12px] text-rd-text flex-1">
            <span className="font-medium">{selectedCount}</span> selected
          </p>
          <button
            type="button"
            onClick={onBulkGenerate}
            disabled={isBulkGenerating || !onBulkGenerate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium text-rd-accent border border-rd-accent/30 bg-rd-accent/10 hover:bg-rd-accent/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Sparkles className={`w-3.5 h-3.5 ${isBulkGenerating ? 'animate-pulse' : ''}`} />
            {isBulkGenerating ? 'Generating…' : `Generate responses for ${selectedCount}`}
          </button>
          <button
            type="button"
            onClick={onClearSelection}
            className="text-[12px] text-rd-dim hover:text-rd-muted transition-colors"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table header */}
      <div
        className="grid gap-0 px-4 py-2.5 border-b border-white/[0.07] bg-rd-surface2 flex-shrink-0 text-[11px] font-medium text-rd-dim uppercase tracking-wider"
        style={{ gridTemplateColumns: gridColumns }}
      >
        {selectionEnabled && (
          <span className="flex items-center">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleSelectAll}
              className="w-3.5 h-3.5 rounded border-white/20 bg-transparent cursor-pointer accent-rd-accent"
              aria-label="Select all reviews"
            />
          </span>
        )}
        <span>Review</span>
        <SortHeader
          label="Rating"
          active={sortField === 'rating'}
          dir={sortDir}
          onClick={() => toggleSort('rating')}
        />
        <SortHeader
          label="Date posted"
          active={sortField === 'date'}
          dir={sortDir}
          onClick={() => toggleSort('date')}
        />
        <span>Sentiment</span>
        <span>Status</span>
        <span>Workflow</span>
        <span />
      </div>

      {/* Table body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col gap-0">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="px-4 py-3 border-b border-white/[0.04] animate-pulse">
                <div className="h-3 w-48 bg-white/5 rounded mb-2" />
                <div className="h-2.5 w-72 bg-white/[0.03] rounded" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <p className="text-rd-muted text-sm mb-1">No reviews yet</p>
            <p className="text-rd-dim text-xs">
              {onFetchLive ? 'Use "Fetch live review" to import one now.' : 'New reviews are imported automatically.'}
            </p>
          </div>
        )}

        {/* Selected-block header — only shows on the page where the pinned
            block starts, i.e. page 1 (selected rows always sort first) */}
        {!isLoading && safePage === 1 && selected.length > 0 && (
          <div className="px-4 py-1.5 border-b border-white/[0.04] bg-rd-accent/5 text-[10px] font-medium text-rd-accent uppercase tracking-wider">
            {selected.length} selected — pinned to top
          </div>
        )}

        {/* Rows */}
        {!isLoading && pageRows.map((review, i) => {
          const sentiment = review.intent_analysis?.sentiment
          const workflow = deriveWorkflowTag(review)

          // Selected, escalated, and "rest" are each sorted independently and
          // pinned in that order, so a sort can restart low-to-high right after
          // a pinned block ends — mark those boundaries so it reads as separate
          // sorted groups instead of one broken one.
          const globalIndex = (safePage - 1) * PAGE_SIZE + i
          const isSelectedBoundary = selected.length > 0 && (escalated.length + rest.length) > 0 && globalIndex === selected.length
          const isEscalatedBoundary =
            sortField !== null && escalated.length > 0 && rest.length > 0 && globalIndex === selected.length + escalated.length

          return (
            <div key={review.id}>
              {isSelectedBoundary && (
                <div className="px-4 py-1.5 border-b border-white/[0.04] bg-rd-surface2/40 text-[10px] font-medium text-rd-dim uppercase tracking-wider">
                  Other reviews
                </div>
              )}
              {isEscalatedBoundary && (
                <div className="px-4 py-1.5 border-b border-white/[0.04] bg-rd-surface2/40 text-[10px] font-medium text-rd-dim uppercase tracking-wider">
                  Other reviews, sorted
                </div>
              )}
              <div
                onClick={() => onRowClick(review.id)}
                className={`grid gap-0 px-4 py-3 border-b border-white/[0.04] border-l-[3px] cursor-pointer hover:bg-rd-surface2/60 transition-colors items-center ${rowBorderClass(review)}`}
                style={{ gridTemplateColumns: gridColumns }}
              >
                {selectionEnabled && (
                  <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(review.id) ?? false}
                      onChange={() => onToggleSelect?.(review.id)}
                      disabled={!isSelectable(review)}
                      title={!isSelectable(review) ? 'Already posted — cannot regenerate' : undefined}
                      className="w-3.5 h-3.5 rounded border-white/20 bg-transparent cursor-pointer accent-rd-accent disabled:opacity-25 disabled:cursor-not-allowed"
                      aria-label={`Select review from ${review.reviewer_name ?? 'Anonymous'}`}
                    />
                  </span>
                )}
                {/* Reviewer + snippet */}
                <div className="min-w-0 pr-3">
                  <p className="text-[13px] font-medium text-rd-text truncate flex items-center gap-1.5">
                    {review.reviewer_name ?? 'Anonymous'}
                    {review.platform === 'judgeme' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-500/10 text-primary-500 shrink-0">
                        Judge.me
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-rd-muted truncate mt-0.5">
                    {review.review_text ?? '(no review text)'}
                  </p>
                </div>

                {/* Rating */}
                <div><Stars rating={review.rating} /></div>

                {/* Date posted */}
                <div
                  className="text-[11px] text-rd-dim"
                  title={review.review_date ? new Date(review.review_date).toLocaleString() : undefined}
                >
                  {review.review_date
                    ? new Date(review.review_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </div>

                {/* Sentiment */}
                <div>
                  {sentiment ? (
                    <Badge className={SENTIMENT_CLASSES[sentiment] ?? 'bg-white/5 text-rd-muted'}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
                    </Badge>
                  ) : <span className="text-rd-dim text-xs">—</span>}
                </div>

                {/* Status */}
                <div>
                  <Badge className={statusClass(review)}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {statusLabel(review)}
                  </Badge>
                </div>

                {/* Workflow */}
                <div>
                  <span className="text-[11px] text-rd-dim font-mono">{workflow}</span>
                </div>

                {/* Chevron */}
                <div className="flex items-center justify-center text-rd-dim">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Pagination footer */}
      {!isLoading && sorted.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.07] bg-rd-surface2/40 flex-shrink-0 text-[11px] text-rd-muted">
          <span>
            {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="flex items-center justify-center w-6 h-6 rounded-md text-rd-accent2 hover:bg-rd-accent2/10 disabled:text-rd-dim disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-rd-text font-medium">Page {safePage} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="flex items-center justify-center w-6 h-6 rounded-md text-rd-accent2 hover:bg-rd-accent2/10 disabled:text-rd-dim disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
